import { TIME_GRID } from 'src/config/constants.ts'

export default {
  data: () => ({
    isScreenReaderActive: false
  }),

  computed: {
    viewType () {
      // force day-view when screenreader is active
      if (this.isScreenReaderActive) return TIME_GRID.VIEW_TYPES.DAY
      return String(this.$route.query.viewType)
    },

    daysToShow () {
      return Number(this.$route.query.daysToShow)
    },

    startDate () {
      return this.$moment(this.$route.query.startDate)
    },

    isMonthView () {
      return this.viewType === TIME_GRID.VIEW_TYPES.MONTH
    },

    isWeekView () {
      return this.viewType === TIME_GRID.VIEW_TYPES.WEEK
    },

    isDayView () {
      return this.viewType === TIME_GRID.VIEW_TYPES.DAY
    },

    dates () {
      const dates = (startDate, amountBefore, amountAfter) => {
        if (this.isMonthView) {
          const getStartOfMonth = (startDate) => {
            // Start of the calendar month
            return this.$moment([startDate.year(), startDate.month()])
          }
          return [
            getStartOfMonth(startDate).clone().subtract(amountBefore, 'month').startOf('isoWeek'),
            getStartOfMonth(startDate).startOf('isoWeek'),
            getStartOfMonth(startDate).clone().add(amountAfter, 'month').startOf('isoWeek')
          ]
        }

        return [
          startDate.clone().subtract(amountBefore, 'days'),
          startDate,
          startDate.clone().add(amountAfter, 'days')
        ]
      }

      // "Opening" (modals, etc.) an item will set startDate to the date of the item. Make sure to always use
      // the full range of dates.
      if (this.isWeekView) return dates(this.startDate.clone().startOf('isoWeek'), 7, 7)

      if (this.isMonthView) return dates(this.startDate, 1, 1)

      const weekends = 8 - this.daysToShow
      if (this.startDate.day() === 1) return dates(this.startDate, weekends, 1) // monday logic -> prev day should be Friday
      if (this.startDate.day() === this.daysToShow) return dates(this.startDate, 1, weekends) // Friday logic -> next day should be Monday
      return dates(this.startDate, 1, 1) // normal logic
    },

    monthDates () {
      let monthDates = {}
      this.unixDates.forEach((month) => {
        const weeksOfMonth = Array.from({ length: 6 }, (_, i) => this.$moment.unix(month).startOf('isoWeek').add(i, 'weeks'))
        monthDates = { ...monthDates, [month]: weeksOfMonth }
      })
      return monthDates
    },

    unixDates () {
      return this.dates.map(s => s.unix())
    },

    isCalendarGrid () {
      return this.$route.name === 'calendar-grid'
    },

    defaultColumnScale () {
      if (this.isCalendarGrid) return TIME_GRID.CALENDAR.DEFAULT_COLUMN_HEIGHT_SCALE
      return TIME_GRID.TIMETABLE.DEFAULT_COLUMN_HEIGHT_SCALE
    }
  },

  methods: {
    async updateQueryParams (routeQuery = {}, routeParams = {}, routeName) {
      const newRouteQuery = {
        ...this.$route.query,
        ...routeQuery
      }
      const newRouteParams = {
        ...this.$route.params,
        ...routeParams
      }

      // Depending on the caller, route query/params can be string or number but hold the same value
      // To not care about this in all places, we stringify all of them, making comparing possible.
      const stringifyObjectValues = (obj) => {
        if (!obj) return {}
        return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, String(value)]))
      }

      // Only update query if it's different from the current one
      const routeQueryChanged = !this.$compare(stringifyObjectValues(newRouteQuery), stringifyObjectValues(this.$route.query))
      const routeParamsChanged = !this.$compare(stringifyObjectValues(newRouteParams), stringifyObjectValues(this.$route.params))
      if (!routeQueryChanged && !routeParamsChanged) return

      await this.$router.replace({
        name: routeName || this.$route.name,
        query: newRouteQuery,
        params: newRouteParams
      })
    },

    getWeekItems (allItems, startDate, emptyArray) {
      const weeks = emptyArray.reduce((acc, _, i) => {
        const weeksToAddNum = this.isMonthView ? i : i - 1
        const dateWeek = this.$moment(startDate).clone().add(weeksToAddNum, 'weeks')
        const week = Number(this.$dateFormat.week_iso(dateWeek))
        return { ...acc, [week]: i }
      }, {})

      return allItems
        .reduce((acc, unit) => {
          return acc.map((week, i) => {
            if (i !== weeks[unit.week]) return week
            return week.map((el, i) => {
              if (i !== unit.weekday - 1) return el
              return [...el, unit]
            })
          })
        }, emptyArray)
    },

    getDayItems (allItems, startDate, emptyArray) {
      const weeks = emptyArray.reduce((acc, _, i) => {
        const dateDay = this.$moment(startDate).clone().add(i - 1, 'days')
        const week = dateDay.isoWeek()
        const day = dateDay.isoWeekday()
        return { ...acc, [week]: { ...acc[week], [day]: i } }
      }, {})

      return allItems.reduce((acc, item) => {
        const { week, weekday } = item
        if (weeks[week]?.[weekday] === undefined) return acc

        const index = weeks[week][weekday]
        // if acc[index][0][0] is the empty dummy object, overwrite it
        Object.keys(acc[index][0][0]).length ? acc[index].push([item]) : (acc[index] = [[item]])
        return acc
      }, emptyArray)
    },

    async getItemRowsPerOverlap (allItems, startDate, datesOfContainer, isDayView, daysToShow, maxEventsPerDay = -1) {
      return new Promise((resolve) => {
        if (!allItems) return resolve(Array.from({ length: 3 }, () => Array(daysToShow).fill([])))

        // apply default sorting to all items so rendered elements don't jump around
        const sortedItems = allItems.sort()

        // day view
        if (isDayView) return resolve(this.getDayItems(sortedItems, startDate, Array.from({ length: 3 }).fill([[{}]])))

        // week view
        const processedAllDayItems = []
        const weekLessons = this.getWeekItems(sortedItems, startDate, Array.from({ length: datesOfContainer.length }, () => Array(daysToShow).fill([])))

        // TODO refactor this to be more readable
        return resolve(weekLessons.reduce((acc, week, weekIndex) => {
          acc[weekIndex] = week.reduce((dayAcc, day, dayIndex) => {
            day.forEach((event, eventIndex) => {
              // initialize the first row to ensure it exists even if there are no events that week
              dayAcc[eventIndex] = dayAcc[eventIndex] || Array(daysToShow).fill({})

              const { id, week: eventWeek, daysDuration } = event
              const sameEventOnDifferentWeekdayIndex = processedAllDayItems.findLast(el => el.id === id && el.week === eventWeek)?.eventIndex
              const sameEventOnDifferentWeekday = sameEventOnDifferentWeekdayIndex !== undefined
              const newEvent = {
                ...event,
                firstOccurrence: !sameEventOnDifferentWeekday,
                widthModifier: sameEventOnDifferentWeekday ? 1 : daysDuration || 1,
                parallelItemsCount: day.length,
                isPlusNIndicator: false
              }

              // month view calculates "+N" button depending on "maxEventsPerDay" and is therefore needed
              if (maxEventsPerDay === -1 && this.isMonthView) return

              // if the max amount of events per day is reached, add plus-n-indicator instead of the event
              if (eventIndex === maxEventsPerDay) {
                dayAcc[eventIndex][dayIndex] = {
                  name: `+${day.length - maxEventsPerDay}`,
                  isPlusNIndicator: true,
                  isAllDay: true,
                  firstOccurrence: true,
                  widthModifier: 1,
                  weekday: newEvent.weekday,
                  minutes_begins_at: newEvent.minutes_begins_at
                }
              } else if (sameEventOnDifferentWeekdayIndex && ((eventIndex < maxEventsPerDay) || !this.isMonthView)) {
              // If the element already has an occurrence, we place the next one in the same line, otherwise we place it in the first available line
                dayAcc[sameEventOnDifferentWeekdayIndex][dayIndex] = newEvent
                processedAllDayItems.push({ ...newEvent, eventIndex: sameEventOnDifferentWeekdayIndex })
              } else if ((eventIndex < maxEventsPerDay) || !this.isMonthView) {
                const existingOverlapIndex = dayAcc.findIndex(el => Object.keys(el[dayIndex]).length === 0)
                if (existingOverlapIndex !== -1) {
                  dayAcc[existingOverlapIndex][dayIndex] = newEvent
                  processedAllDayItems.push({ ...newEvent, eventIndex: existingOverlapIndex })
                }
              }
            })
            return dayAcc
          }, [Array(daysToShow).fill({})])
          return acc
        }, Array.from({ length: datesOfContainer.length }, () => [])))
      })
    },

    async getItemsByWeekAndDay (allItems, startDate, datesOfContainer, isWeekView, daysToShow) {
      return new Promise((resolve) => {
        if (!allItems) return resolve(new Array(3).fill(new Array(5).fill([])))

        const emptyArray = new Array(datesOfContainer.length).fill(new Array(daysToShow).fill([]))

        const weekLessons = this.getWeekItems(allItems, startDate, emptyArray)

        const res = Object.values(weekLessons).map(el => Object.values(el))

        if (isWeekView) return resolve(res)

        const day = startDate.isoWeekday() - 1
        const days = emptyArray.map((el, i) => {
          const d = day + i - 1
          if (d < 0) return { week: 0, day: daysToShow - 1 }
          if (d > daysToShow - 1) return { week: 2, day: 0 }
          return { week: 1, day: d }
        })

        resolve(days.map(({ week, day }) => [res[week][day]]))
      })
    },

    calculateBeginsAtEndsAt (date) {
      if (this.isMonthView) {
        const startOfMonth = this.$moment([date.year(), date.month()]).startOf('isoWeek')
        const beginsAt = this.$dateFormat.yearlong_month_date(startOfMonth)
        const endsAt = this.$dateFormat.yearlong_month_date(startOfMonth.clone().add(6, 'weeks').subtract(1, 'days'))
        return {
          fetch_range_begins_at: beginsAt,
          fetch_range_ends_at: endsAt,
          begins_at: beginsAt,
          ends_at: endsAt
        }
      }

      const startOfWeek = date.clone().startOf('isoWeek')
      const endOfWeek = date.clone().endOf('isoWeek')

      const beginsAt = this.$dateFormat.yearlong_month_date(startOfWeek.clone().subtract(3, 'week'))
      const endsAt = this.$dateFormat.yearlong_month_date(endOfWeek.clone().add(3, 'week'))

      return {
        fetch_range_begins_at: beginsAt,
        fetch_range_ends_at: endsAt,
        begins_at: beginsAt,
        ends_at: endsAt
      }
    },

    getFetchRangeByViewType (date, isFuture = false) {
      if (this.isMonthView) {
        const startOfMonth = this.$moment([date.year(), date.month()]).startOf('isoWeek')
        const beginsAt = this.$dateFormat.yearlong_month_date(startOfMonth)
        const endsAt = this.$dateFormat.yearlong_month_date(startOfMonth.clone().add(6, 'weeks').subtract(1, 'days'))
        return { beginsAt, endsAt }
      }

      const startOfWeek = date.clone().startOf('isoWeek')
      const endOfWeek = date.clone().endOf('isoWeek')
      if (isFuture) {
        const beginsAt = this.$dateFormat.yearlong_month_date(startOfWeek.clone().add(1, 'week'))
        const endsAt = this.$dateFormat.yearlong_month_date(endOfWeek.clone().add(3, 'week'))
        return { beginsAt, endsAt }
      }
      const beginsAt = this.$dateFormat.yearlong_month_date(startOfWeek.clone().subtract(3, 'week'))
      const endsAt = this.$dateFormat.yearlong_month_date(endOfWeek.clone().subtract(1, 'week'))
      return { beginsAt, endsAt }
    },

    calculateBeginsAtEndsAtPast (date, timeRange) {
      const { beginsAt, endsAt } = this.getFetchRangeByViewType(date)
      return {
        fetch_range_begins_at: beginsAt,
        fetch_range_ends_at: timeRange.fetch_range_ends_at,
        begins_at: beginsAt,
        ends_at: endsAt
      }
    },

    calculateBeginsAtEndsAtFuture (date, timeRange) {
      const { beginsAt, endsAt } = this.getFetchRangeByViewType(date, true)
      return {
        fetch_range_begins_at: timeRange.fetch_range_begins_at,
        fetch_range_ends_at: endsAt,
        begins_at: beginsAt,
        ends_at: endsAt
      }
    },

    async getWeekContainers (itemsByWeekAndDay) {
      return new Promise((resolve) => {
        const weekContainers = itemsByWeekAndDay.map(week => {
          return week.map(itemsByWeek => this.calcGridThreadLayout(itemsByWeek))
        })
        resolve(weekContainers)
      })
    },

    calcGridThreadLayout (itemsByWeek) {
      let layout = []

      const overLaps = (a_start, a_end, b_start, b_end) => {
        return (a_start <= b_start && a_end > b_start) || (a_start < b_end && a_end >= b_end) || (b_start <= a_start && b_end > a_start) || (b_start < a_end && b_end >= a_end)
      }

      const compareDates = (a, b) => {
        return overLaps(a.minutes_begins_at, a.minutes_ends_at, b.minutes_begins_at, b.minutes_ends_at)
      }

      itemsByWeek.map(item => {
        const containers = layout.filter(el => compareDates(item, el))

        if (containers.length !== 0) {
          const otherContainer = layout.filter(el => !compareDates(item, el))
          const combinedContainer = containers.reduce((acc, el) => {
            const list = acc.thread

            el.thread.forEach((element, i) => {
              if (!list[i]) list[i] = []

              list[i] = [...list[i], ...element]
            })

            return {
              thread: list,
              minutes_begins_at: el.minutes_begins_at < acc.minutes_begins_at ? el.minutes_begins_at : acc.minutes_begins_at,
              minutes_ends_at: el.minutes_ends_at > acc.minutes_ends_at ? el.minutes_ends_at : acc.minutes_ends_at
            }
          }, {
            minutes_begins_at: item.minutes_begins_at,
            minutes_ends_at: item.minutes_ends_at,
            thread: []
          })

          layout = [...otherContainer, combinedContainer]
        }

        let containerID = layout.findIndex(el => compareDates(item, el))

        if (containerID === -1) {
          containerID = layout.length
          layout.push({
            minutes_begins_at: item.minutes_begins_at,
            minutes_ends_at: item.minutes_ends_at,
            thread: []
          })
        }

        const elementIndex = layout[containerID].thread.findIndex(thread =>
          thread.findIndex((element) => {
            return !compareDates(element, item)
          }) !== -1 && thread.every(element => !compareDates(element, item))
        )

        if (elementIndex === -1) { return layout[containerID].thread.push([item]) }

        if (layout[containerID].minutes_begins_at > item.minutes_begins_at) layout[containerID].minutes_begins_at = item.minutes_begins_at
        if (layout[containerID].minutes_ends_at < item.minutes_ends_at) layout[containerID].minutes_ends_at = item.minutes_ends_at

        return layout[containerID].thread[elementIndex].push(item)
      })

      // return layout
      return layout.map(el => el.thread.map(t => t.map(element => element)))
    },

    getCellItemStyles (cellItemMinutesBeginsAt, cellItemMinutesEndsAt, dayMinutesBeginsAt) {
      return {
        height: `${(1 / 16) * parseInt((cellItemMinutesEndsAt - cellItemMinutesBeginsAt) * this.defaultColumnScale)}rem`,
        top: `${(1 / 16) * parseInt((cellItemMinutesBeginsAt - dayMinutesBeginsAt) * this.defaultColumnScale)}rem`,
      }
    },

    getWidthByEventDuration (dayItem) {
      if (!dayItem || this.isDayView) return '100%'
      if (!dayItem.widthModifier) return '0%'
      return `calc(100% * ${Number(dayItem.widthModifier)})`
    }
  },

  beforeMount () {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  watch: {
    '$store.state.isScreenReaderActive' (newVal) {
      this.isScreenReaderActive = newVal
    }
  }
}
